// src/components/StockAnr.jsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { MaterialReactTable } from "material-react-table";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Barcode from "react-barcode";
import {
  Printer,
  Search,
  Edit,
  Trash2,
  Copy,
  PlusCircle,
  MinusCircle,
  X,
} from "lucide-react";
import { Box, Card, useTheme } from "@mui/material";
import { tokens } from "../../theme";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getToken, base_url } from "../../config/config";
import QualityNoteCard from "../Anr/QualityNote";

const InputField = React.memo(
  ({ label, name, value, onChange, type = "text", placeholder, ...rest }) => (
    <div>
      {label && <label className="block text-sm font-medium">{label}</label>}
      <input
        type={type}
        name={name}
        value={value}
        placeholder={placeholder}
        onChange={onChange}
        {...rest}
        className="mt-1 block w-full border border-gray-300 bg-white px-4 py-3 text-base text-black focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-150 ease-in-out rounded-none"
      />
    </div>
  ),
);

const StockForm = React.memo(
  ({
    formData,
    setFormData,
    handleSubmit,
    selectedKunde,
    setShowKundeModal,
    maxLohnaufwandUnit,
    setMaxLohnaufwandUnit,
    shippingMethod,
    setShippingMethod,
    packagingType,
    setPackagingType,
    externalService,
    setExternalService,
    handleQualityDataChange,
    editMode = false,
    initialExternalService = [],
    latestANR,
    latestStockANR,
    nextRegularANR,
    nextStockANR,
  }) => {
    const handleInputChange = useCallback(
      (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
      },
      [setFormData],
    );

    const normalizeExternalService = (value) => {
      if (Array.isArray(value)) {
        return value;
      } else if (typeof value === "string" && value.trim()) {
        try {
          // Try to parse as JSON first (in case it's a stringified array)
          const parsed = JSON.parse(value);
          return Array.isArray(parsed) ? parsed : [value];
        } catch (e) {
          // If parsing fails, treat as single string value
          return [value];
        }
      }
      return [];
    };

    // Initialize external service when in edit mode
    useEffect(() => {
      if (editMode && initialExternalService) {
        const normalized = normalizeExternalService(initialExternalService);
        setExternalService(normalized);
      }
    }, [editMode, initialExternalService, setExternalService]);

    return (
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Field 1: MENGE kosten / H */}
          <div>
            <label className="block text-sm font-medium pb-4">
              MENGE kosten / H
            </label>
            <InputField
              name="MengeCost"
              value={formData.MengeCost}
              onChange={handleInputChange}
              placeholder="z.B. 60"
            />
          </div>

          {/* Field 2a: Next Stock ANR (Display Only) */}
          <div>
            <label className="block text-sm font-medium">Lager ANR</label>
            <p className="text-xs">(LA-XXXX-XX-XX-XX)</p>
            <InputField
              name="nextStockANR"
              value={nextStockANR || ""}
              disabled
              placeholder="Wird beim Erstellen generiert"
            />
          </div>

          {/* Field 2c: Next Regular ANR (Display Only) */}
          <div>
            <label className="block text-sm font-medium">ANR</label>
            <p className="text-xs">(XXXX-XX-XX-XX)</p>
            <InputField
              name="nextRegularANR"
              value={nextRegularANR || ""}
              disabled
              placeholder="Wird beim Erstellen generiert"
            />
          </div>

          {/* Field 3: Werkstückbezeichnung */}
          <InputField
            label="Werkstückbezeichnung"
            name="form_auftragsbezeichnung"
            value={formData.form_auftragsbezeichnung}
            onChange={handleInputChange}
            placeholder="Beschreibung des Stock-Auftrags"
          />

          {/* Field 4a: Menge Kundenauftrag */}
          <InputField
            label="Menge Kundenauftrag"
            name="MengeKundenauftrag"
            value={formData.MengeKundenauftrag}
            onChange={handleInputChange}
            placeholder="Kundenauftragsmenge"
          />

          {/* Field 4: Menge */}
          <InputField
            label="LagerMenge"
            name="Menge"
            value={formData.Menge}
            onChange={handleInputChange}
            placeholder="Bestellmenge"
          />

          {/* Field 5: Kunde selection */}
          <div>
            <label className="block text-sm font-medium">Kunde</label>
            <button
              type="button"
              onClick={() => setShowKundeModal(true)}
              className="mt-1 block w-full border border-gray-300 px-4 py-3 text-black text-base text-left bg-white transition duration-150 ease-in-out rounded-none"
            >
              {selectedKunde
                ? `${selectedKunde.konto} - ${selectedKunde.bezeichnung}`
                : "Kunde auswählen"}
            </button>
          </div>

          {/* Field 6: Auftragserteilungsdatum */}
          <InputField
            label="Auftragserteilungsdatum"
            name="deadline"
            type="date"
            value={formData.deadline || ""}
            onChange={handleInputChange}
          />

          {/* Field 7: Fertigstellungstermin */}
          <InputField
            label="Fertigstellungstermin"
            name="completionDate"
            type="date"
            value={formData.completionDate || ""}
            onChange={handleInputChange}
          />

          {/* Field 8: Kostenstelle */}
          <InputField
            label="Kostenstelle"
            name="kostenstelle"
            value={formData.kostenstelle || ""}
            onChange={handleInputChange}
          />

          <InputField
            label="Lieferdatum"
            name="Lieferdatum"
            type="date"
            value={formData.Lieferdatum || ""}
            onChange={handleInputChange}
          />

          <InputField
            label="Auftragserlös"
            name="Auftragserlös"
            value={formData.Auftragserlös || ""}
            onChange={handleInputChange}
          />

          {/* Field 9: Artikelnummer */}
          <InputField
            label="Artikelnummer"
            name="Artikelnummer"
            value={formData.Artikelnummer}
            onChange={handleInputChange}
            placeholder="Artikelnummer eingeben"
          />

          {/* Field 10: Zeichnungs-Nr */}
          <InputField
            label="Zeichnungs-Nr"
            name="form_baugruppenzeichnung"
            value={formData.form_baugruppenzeichnung}
            onChange={handleInputChange}
          />

          {/* Field 11: Einzelmengenkosten */}
          <InputField
            label="Einzelmengenkosten"
            name="singlemengecost"
            value={formData.singlemengecost}
            onChange={handleInputChange}
          />

          {/* Field 12: auftragswertNetto */}
          <InputField
            label="Auftragswert netto"
            name="auftragswertNetto"
            value={formData.auftragswertNetto}
            onChange={handleInputChange}
          />

          {/* Field 13: Maximaler Lohnaufwand with Unit Selection */}
          <div className="col-span-2">
            <label className="block text-sm font-medium">
              Maximaler Lohnaufwand
            </label>
            <div className="flex space-x-4">
              <InputField
                name="maxLohnaufwand"
                value={formData.maxLohnaufwand}
                onChange={handleInputChange}
                type="number"
                placeholder="Wert in Zahlen"
              />
              <select
                name="maxLohnaufwandUnit"
                value={maxLohnaufwandUnit}
                onChange={(e) => setMaxLohnaufwandUnit(e.target.value)}
                className="mt-1 block w-full border border-gray-300 bg-white px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-150 ease-in-out rounded-none"
              >
                <option value="hours">Stunden</option>
                <option value="minutes">Minuten</option>
              </select>
            </div>
          </div>

          <div className="col-span-2">
            {/* shippingMethod */}
            <label className="block text-sm font-medium">Versandart</label>
            <div className="flex space-x-4">
              <select
                name="Versandart"
                value={shippingMethod}
                onChange={(e) => setShippingMethod(e.target.value)}
                className="mt-1 block w-full border border-gray-300 bg-white px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-150 ease-in-out rounded-none"
              >
                <option value="" disabled hidden>
                  Versandart wählen
                </option>
                <option value="selbstabholung">Selbstabholung</option>
                <option value="exwabwerk">EXW - ab Werk</option>
                <option value="freihaus">Frei Haus</option>
              </select>
            </div>
          </div>

          <div className="col-span-2">
            {/* packagingType */}
            <label className="block text-sm font-medium">Verpackungsart</label>
            <div className="flex space-x-4">
              <select
                name="Verpackungsart"
                value={packagingType}
                onChange={(e) => setPackagingType(e.target.value)}
                className="mt-1 block w-full border border-gray-300 bg-white px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-150 ease-in-out rounded-none"
              >
                <option value="" disabled hidden>
                  Verpackungsart wählen
                </option>
                <option value="ohne">Ohne</option>
                <option value="einwegpalette">Einwegpalette</option>
                <option value="europalette">Europalette</option>
                <option value="rahmen">Europalette mit Rahmen</option>
                <option value="DHL-kleines">DHL Versand - kleines Paket</option>
                <option value="DHL-großes">DHL Versand - großes Paket</option>
              </select>
            </div>
          </div>

          <div className="col-span-2">
            {/* external Service - Multi Select */}
            <label className="block text-sm font-medium">Fremdleistung</label>
            <div className="flex space-x-4">
              <select
                name="Fremdleistung"
                value=""
                onChange={(e) => {
                  if (
                    e.target.value &&
                    !externalService.includes(e.target.value)
                  ) {
                    setExternalService([...externalService, e.target.value]);
                  }
                }}
                className="mt-1 block w-full border border-gray-300 bg-white px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-150 ease-in-out rounded-none"
              >
                <option value="" disabled>
                  Fremdleistung hinzufügen
                </option>
                <option value="ohne">Ohne</option>
                <option value="normalisieren">Normalisieren</option>
                <option value="harten">Härten</option>
                <option value="gasnitrieren">Gasnitrieren</option>
                <option value="verzahnen">Verzahnen</option>
                <option value="teiflochbohren">Teiflochbohren</option>
                <option value="fremdleistungen">Fremdleistungen</option>
              </select>
            </div>

            {/* Display selected external services */}
            {externalService.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {externalService.map((service, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                  >
                    {service}
                    <button
                      type="button"
                      onClick={() => {
                        setExternalService(
                          externalService.filter((_, i) => i !== index),
                        );
                      }}
                      className="ml-2 text-blue-600 hover:text-blue-800"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Quality Note Section */}
          <div className="col-span-2">
            <QualityNoteCard onQualityDataChange={handleQualityDataChange} />
          </div>

          {/* Field 14: Beschreibung */}
          <div className="col-span-2">
            <label className="block text-sm font-medium">Beschreibung</label>
            <textarea
              name="form_erläuterung"
              value={formData.form_erläuterung}
              onChange={handleInputChange}
              rows={3}
              placeholder="Zusätzliche Informationen zum Stock-Auftrag"
              className="mt-1 block w-full border border-gray-300 bg-white px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-150 ease-in-out rounded-none"
            />
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            className="w-full py-3 px-4 border border-transparent rounded-none shadow-sm text-base font-medium text-white bg-blue-600 hover:bg-blue-700 transition duration-150 ease-in-out"
          >
            Auftrag erstellen
          </button>
        </div>
      </form>
    );
  },
);

const StockANRDisplay = React.memo(
  ({
    color,

    clearForm,
  }) => (
    <Card
      elevation={3}
      sx={{
        bgcolor: color,
        transition: "all 0.3s",
      }}
    >
      <div className="p-6">
        <div className="flex space-x-3">
          <button
            onClick={clearForm}
            className="inline-flex items-center px-4 py-3 border border-transparent rounded-none shadow-sm text-base font-medium text-white bg-red-600 hover:bg-red-700"
          >
            <X className="w-4 h-4 mr-2" />
            Alles Löschen
          </button>
        </div>
      </div>
    </Card>
  ),
);

// -----------------------------
// Main Component
// -----------------------------
function StockAnr() {
  // Form & stock order state
  const [formData, setFormData] = useState({
    ANR: "",
    MengeCost: "0",
    Menge: "0",
    MengeKundenauftrag: "0",
    form_auftragsbezeichnung: "",
    form_erläuterung: "",
    Artikelnummer: "",
    Lieferdatum: "",
    deadline: "",
    completionDate: "",
    kostenstelle: "",
    Auftragserlös: "",
    form_baugruppenzeichnung: "",
    singlemengecost: "",
    auftragswertNetto: "",
    maxLohnaufwand: "",
  });
  const [stockOrders, setStockOrders] = useState([]);
  const [regularProjects, setRegularProjects] = useState([]);
  const [editOrder, setEditOrder] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [error, setError] = useState(null);
  const normalizeExternalService = (value) => {
    if (Array.isArray(value)) {
      return value;
    } else if (typeof value === "string" && value.trim()) {
      try {
        // Try to parse as JSON first (in case it's a stringified array)
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [value];
      } catch (e) {
        // If parsing fails, treat as single string value
        return [value];
      }
    }
    return [];
  };

  // Pagination state
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [loading, setLoading] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Stock ANR state
  const [anrParts, setAnrParts] = useState({
    firstNumber: "",
    secondNumber: "",
    thirdNumber: "",
    year: new Date().getFullYear().toString().slice(-2),
  });
  const [latestStockANR, setLatestStockANR] = useState(null);

  // Print dialog state
  const [isPrintDialogOpen, setIsPrintDialogOpen] = useState(false);
  const [selectedPrintFields, setSelectedPrintFields] = useState([]);
  const [selectedProjectForPrint, setSelectedProjectForPrint] = useState(null);

  // Additional form state variables
  const [selectedKunde, setSelectedKunde] = useState(null);
  const [showKundeModal, setShowKundeModal] = useState(false);
  const [kundenList, setKundenList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredKunden, setFilteredKunden] = useState([]);
  const [confirmed, setConfirmed] = useState(false);
  const [maxLohnaufwandUnit, setMaxLohnaufwandUnit] = useState("hours");
  const [shippingMethod, setShippingMethod] = useState("");
  const [packagingType, setPackagingType] = useState("");
  const [externalService, setExternalService] = useState([]);
  const [qualityData, setQualityData] = useState({});
  const [gabsKunde, setGabsKunde] = useState(null);

  const token = getToken();
  const navigate = useNavigate();
  const location = useLocation();

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // Function to get page heading based on current route
  const getPageHeading = () => {
    const path = location.pathname;
    if (path.includes("/stock/addAuftrage")) {
      return "Lager Erstellen";
    } else if (path.includes("/stock/stockAnr")) {
      return "Auftrag und Lager Erstellen";
    } else {
      return "Auftrag Erstellen";
    }
  };

  const availablePrintFields = [
    { key: "ANR", label: "ANR" },
    { key: "Auftragsbezeichnung", label: "Auftragsbezeichnung" },
    { key: "Menge", label: "Menge" },
    { key: "MengeKundenauftrag", label: "Menge Kundenauftrag" },
    { key: "invoiceNo", label: "Rechnungsnummer" },
    { key: "percentDone", label: "Fortschritt (%)" },
    { key: "status", label: "Status" },
    { key: "deadline", label: "Auftragserteilungsdatum" },
    { key: "Lieferdatum", label: "Lieferdatum" },
    { key: "completionDate", label: "Fertigstellungstermin" },
    { key: "kostenstelle", label: "Kostenstelle" },
    { key: "auftragswertNetto", label: "auftragswertNetto" },
    { key: "MaxLohnaufwand", label: "Maximaler Lohnaufwand" },
    { key: "singlemengecost", label: "Einzelmengenkosten" },
    { key: "shippingMethod", label: "Versandart" },
    { key: "packagingType", label: "Verpackungsart" },
    { key: "externalService", label: "Fremdleistung" },
    { key: "qualityNote", label: "Qualitätsnotiz" },
    { key: "Erläuterung", label: "Beschreibung" },
  ];

  const navigateToStockAuftraege = useCallback(() => {
    navigate("/addanr");
  }, [navigate]);

  // Print barcode for stock documents
  const printStockDocumentsBarcode = useCallback(() => {
    const printWindow = window.open("", "PRINT", "height=800,width=800");
    printWindow.document.write(
      "<html><head><title>Stock-documents Barcode</title>",
    );
    printWindow.document.write(`
    <style>
        @page { size: 8in 4in; margin: 8mm; }
        body { width: 8in; margin: 0; padding: 0; line-height: 1; }
        .barcode { text-align: center; }
        .barcode svg { width: 100% !important; height: 250px !important; }
        .barcode p { font-size: 16px; font-weight: bold; margin: 8px 0; }
      </style>
  `);
    printWindow.document.write("</head><body>");
    const barcodeElement = document.getElementById("barcode-stock-documents");
    if (barcodeElement) {
      printWindow.document.write('<div class="barcode">');
      printWindow.document.write(barcodeElement.innerHTML);
      printWindow.document.write("<p>stock-documents</p>");
      printWindow.document.write("</div>");
    } else {
      console.error("Barcode element for stock-documents not found.");
    }
    printWindow.document.write("</body></html>");
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  }, []);

  const handleQualityDataChange = (data) => {
    setQualityData(data);
  };
  const [latestANR, setLatestANR] = useState(null);
  const [nextRegularANR, setNextRegularANR] = useState(null);
  const [nextStockANR, setNextStockANR] = useState(null);

  // Clear form function
  const clearForm = useCallback(() => {
    // Reset form data to initial state
    setFormData({
      MengeCost: "0",
      Menge: "0",
      MengeKundenauftrag: "0",
      form_auftragsbezeichnung: "",
      form_erläuterung: "",
      Artikelnummer: "",
      Lieferdatum: "",
      deadline: "",
      completionDate: "",
      kostenstelle: "",
      Auftragserlös: "",
      form_baugruppenzeichnung: "",
      singlemengecost: "",
      auftragswertNetto: "",
      maxLohnaufwand: "",
    });

    // Reset other form-related state
    setSelectedKunde(null);
    setMaxLohnaufwandUnit("hours");
    setShippingMethod("");
    setPackagingType("");
    setExternalService([]);
    setQualityData({});

    toast.success("Formular wurde zurückgesetzt");
  }, []);

  // Customer modal handlers
  const handleSelectKunde = useCallback((kunde) => {
    console.log("Selected Kunde:", kunde);
    setSelectedKunde(kunde);
    setConfirmed(false);
  }, []);

  const handleConfirmKunde = useCallback(() => {
    console.log("Confirmed Kunde:", selectedKunde);
    setConfirmed(true);
    setFormData((prev) => ({ ...prev, kundenID: selectedKunde._id }));
    setShowKundeModal(false);
  }, [selectedKunde]);

  const handleClearKunde = useCallback(() => {
    setSelectedKunde(null);
    setConfirmed(false);
    setSearchTerm("");
    setFilteredKunden([]);
  }, []);

  // Filter customers based on search term
  useEffect(() => {
    if (searchTerm.trim()) {
      const filtered = kundenList.filter(
        (kunde) =>
          kunde.bezeichnung.toLowerCase().includes(searchTerm.toLowerCase()) ||
          kunde.konto.includes(searchTerm),
      );
      setFilteredKunden(filtered);
    } else {
      setFilteredKunden([]);
    }
  }, [kundenList, searchTerm]);

  useEffect(() => {
    const fetchLatestStockANR = async () => {
      try {
        const response = await fetch(
          `${base_url}/v1/stock/get-latest-stock-ANR`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          },
        );
        if (!response.ok)
          throw new Error("Fehler beim Abrufen der neuesten Stock-ANR");
        const data = await response.json();
        if (data.Number) {
          const [LA, first, second, third] = data.Number.split("-");
          setAnrParts({
            firstNumber: first,
            secondNumber: second,
            thirdNumber: third,
            year: new Date().getFullYear().toString().slice(-2),
          });
          setFormData((prev) => ({
            ...prev,
            ANR: data.Number,
            form_auftragsbezeichnung: data.AnrText || "",
          }));
          setLatestStockANR(data.Number);
        }
      } catch (error) {
        console.error("Fehler beim Abrufen der neuesten Stock-ANR:", error);
      }
    };
    const fetchKunden = async () => {
      try {
        const response = await fetch(`${base_url}/v1/getKunden`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await response.json();
        setKundenList(data.data);
        data.data.forEach((kunde) => {
          if (
            kunde.bezeichnung &&
            kunde.bezeichnung.trim() != "" &&
            kunde.bezeichnung.trim() === "GABS GmbH"
          ) {
            setGabsKunde(kunde);
          }
        });
      } catch (error) {
        setError("Fehler beim Laden der Kunden.");
        console.error("Fehler beim Abrufen der Kunden:", error);
      }
    };
    fetchLatestStockANR();
    fetchKunden();
  }, []);

  // Fetch stock orders
  const fetchStockOrders = useCallback(async () => {
    setLoading(true);
    try {
      // Build query parameters for pagination
      const queryParams = new URLSearchParams({
        page: (pagination.pageIndex + 1).toString(),
        limit: pagination.pageSize.toString(),
        isStockAuftrag: "true",
      });

      const response = await axios.get(
        `${base_url}/v1/getAllProjects?${queryParams}`,
      );

      if (response.data.status === 1) {
        const { data, pagination } = response.data;
        const { totalOrders, totalPages } = pagination;

        setStockOrders(data || []);
        setRowCount(totalOrders || 0);
        setTotalPages(totalPages || 0);
      }
    } catch (error) {
      console.error("Fehler beim Abrufen der Stock-Aufträge:", error);
    } finally {
      setLoading(false);
    }
  }, [pagination.pageIndex, pagination.pageSize]);

  // Fetch regular projects
  const fetchRegularProjects = useCallback(async () => {
    try {
      // Build query parameters for recent regular projects (limit to 5 for display)
      const queryParams = new URLSearchParams({
        page: "1",
        limit: "5",
        isStockAuftrag: "false",
      });

      const response = await axios.get(
        `${base_url}/v1/getAllProjects?${queryParams}`,
      );

      if (response.data.status === 1) {
        const { data } = response.data;
        setRegularProjects(data || []);
      }
    } catch (error) {
      console.error("Fehler beim Abrufen der regulären Projekte:", error);
    }
  }, []);

  useEffect(() => {
    fetchStockOrders();
    fetchRegularProjects();
  }, [fetchStockOrders, fetchRegularProjects]);

  const checkStockANRExists = useCallback(
    (anr) => stockOrders.some((order) => order.ANR === anr),
    [stockOrders],
  );

  const copyStockANR = useCallback(() => {
    navigator.clipboard.writeText(formData.ANR);
    toast.success("Stock-ANR kopiert!");
  }, [formData.ANR]);

  const createStockANR = useCallback(
    async (ANR) => {
      try {
        await fetch(`${base_url}/v1/stock/create-ANR`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            Number: ANR,
            AnrText: formData.form_auftragsbezeichnung,
          }),
        });
      } catch (error) {
        console.error("Fehler beim Erstellen der Stock-ANR:", error);
        throw error;
      }
    },
    [formData.form_auftragsbezeichnung],
  );
  useEffect(() => {
    const fetchLatestANR = async () => {
      try {
        const response = await fetch(`${base_url}/v1/searchAnr`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ last: 1 }),
        });
        if (!response.ok)
          throw new Error("Fehler beim Abrufen der neuesten ANR");
        const data = await response.json();
        if (data.Number) {
          const [first, second, third, year] = data.Number.split("-");
          setAnrParts({
            firstNumber: first,
            secondNumber: second,
            thirdNumber: third,
            year: year || new Date().getFullYear().toString().slice(-2),
          });
          setFormData((prev) => ({
            ...prev,
            ANR: data.Number,
            form_auftragsbezeichnung: data.AnrText || "",
          }));
          setLatestANR(data.Number);
        }
      } catch (error) {
        console.error("Fehler beim Abrufen der neuesten ANR:", error);
      }
    };

    const fetchLatestStockANR = async () => {
      try {
        const response = await fetch(
          `${base_url}/v1/stock/get-latest-stock-ANR`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          },
        );
        if (!response.ok)
          throw new Error("Fehler beim Abrufen der neuesten Stock-ANR");
        const data = await response.json();
        if (data.Number) {
          const [LA, first, second, third] = data.Number.split("-");
          setAnrParts({
            firstNumber: first,
            secondNumber: second,
            thirdNumber: third,
            year: new Date().getFullYear().toString().slice(-2),
          });
          setFormData((prev) => ({
            ...prev,
            ANR: data.Number,
            form_auftragsbezeichnung: data.AnrText || "",
          }));
          setLatestStockANR(data.Number);
        }
      } catch (error) {
        console.error("Fehler beim Abrufen der neuesten Stock-ANR:", error);
      }
    };

    const fetchData = async () => {
      await Promise.all([fetchLatestStockANR(), fetchLatestANR()]);
    };

    fetchData();
  }, []);
  const incrementANR = useCallback((anrNumber) => {
    if (!anrNumber) return anrNumber;

    const parts = anrNumber.split("-");

    // Check if it's a stock ANR format (starts with "LA-")
    if (anrNumber.startsWith("LA-") && parts.length >= 4) {
      // Stock ANR format: LA-0006-00-00-25 -> LA-0007-00-00-25
      const secondPart = parseInt(parts[1], 10);
      const incrementedSecond = (secondPart + 1).toString().padStart(4, "0");
      return `${parts[0]}-${incrementedSecond}-00-00-${parts[3]}`;
    } else if (parts.length >= 4) {
      // Regular ANR format: 0397-33-23-24 -> 0398-00-00-24
      const firstPart = parseInt(parts[0], 10);
      const incrementedFirst = (firstPart + 1).toString().padStart(4, "0");
      return `${incrementedFirst}-${parts[1]}-${parts[2]}-${parts[3]}`;
    }

    return anrNumber;
  }, []);

  // Calculate incremented ANRs when latest ANRs are available
  useEffect(() => {
    if (latestANR) {
      setNextRegularANR(incrementANR(latestANR));
    }
    if (latestStockANR) {
      setNextStockANR(incrementANR(latestStockANR));
    }
  }, [latestANR, latestStockANR, incrementANR]);

  // Helper function to increment ANR based on format

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (checkStockANRExists(nextStockANR)) {
        toast.error(`Stock-ANR ${nextStockANR} existiert bereits!`, {
          autoClose: 3000,
        });
        return;
      }

      try {
        // Use the pre-calculated incremented ANRs
        const newRegularANR = nextRegularANR;
        const newStockANR = nextStockANR;
        console.log("New Regular ANR:", newRegularANR);
        console.log("New Stock ANR:", newStockANR);
        // Step 4: Create regular ANR
        await fetch(`${base_url}/v1/createAnr`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            Number: newRegularANR,
            AnrText: formData.form_auftragsbezeichnung,
          }),
        });

        // Step 5: Create stock ANR
        await fetch(`${base_url}/v1/stock/create-ANR`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            Number: newStockANR,
            AnrText: formData.form_auftragsbezeichnung,
          }),
        });

        // Step 6: Create FormData for the regular project (using regular ANR and Menge)
        const regularProjectFormData = new FormData();
        regularProjectFormData.append("ANR", newRegularANR);
        regularProjectFormData.append("Menge", formData.MengeKundenauftrag);
        // regularProjectFormData.append(
        //   "MengeKundenauftrag",
        //   formData.MengeKundenauftrag
        // );
        regularProjectFormData.append(
          "Auftragsbezeichnung",
          formData.form_auftragsbezeichnung,
        );
        regularProjectFormData.append(
          "Baugruppenzeichnung",
          formData.form_baugruppenzeichnung || "",
        );
        regularProjectFormData.append("Erlauterung", formData.form_erläuterung);
        regularProjectFormData.append("Artikelnummer", formData.Artikelnummer);
        regularProjectFormData.append("MengeCost", formData.MengeCost);
        regularProjectFormData.append(
          "singlemengecost",
          formData.singlemengecost || "0",
        );
        regularProjectFormData.append(
          "MaxLohnaufwand",
          formData.maxLohnaufwand || "0",
        );
        regularProjectFormData.append("maxlohnfundUnit", maxLohnaufwandUnit);
        regularProjectFormData.append("status", formData.status);
        regularProjectFormData.append("percentDone", formData.percentDone);
        regularProjectFormData.append("invoiceNo", formData.invoiceNo);
        regularProjectFormData.append("totalMaterialCost", 0);
        regularProjectFormData.append("totalsubcontratorCost", 0);
        regularProjectFormData.append("kundenID", formData.kundenID || "");
        regularProjectFormData.append("deadline", formData.deadline || "");
        regularProjectFormData.append(
          "completionDate",
          formData.completionDate || "",
        );
        regularProjectFormData.append(
          "kostenstelle",
          formData.kostenstelle || "",
        );
        regularProjectFormData.append(
          "Lieferdatum",
          formData.Lieferdatum || "",
        );
        regularProjectFormData.append(
          "Auftragserlös",
          formData.Auftragserlös || "0",
        );
        regularProjectFormData.append(
          "auftragswertNetto",
          formData.auftragswertNetto || "0",
        );

        // Regular project - NOT marked as Stock-Auftrag
        regularProjectFormData.append("isStockAuftrag", "false");

        // Add the additional state values
        regularProjectFormData.append("shippingMethod", shippingMethod || "");
        regularProjectFormData.append("packagingType", packagingType || "");

        // Send externalService as individual array items (FormData will handle arrays properly)
        if (externalService && Array.isArray(externalService)) {
          externalService.forEach((service, index) => {
            regularProjectFormData.append(`externalService[${index}]`, service);
          });
        }

        // Handle quality data separately to extract signature
        const qualityDataCopy = { ...qualityData };
        let signatureFile = null;

        if (
          qualityData.signature &&
          qualityData.signature.startsWith("data:image/png;base64,")
        ) {
          try {
            const base64Data = qualityData.signature.split(",")[1];
            const byteCharacters = atob(base64Data);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: "image/png" });

            signatureFile = new File(
              [blob],
              `signature_${newRegularANR}_${Date.now()}.png`,
              {
                type: "image/png",
              },
            );

            delete qualityDataCopy.signature;
          } catch (error) {
            console.error("Error converting signature to file:", error);
          }
        }

        regularProjectFormData.append(
          "qualityNote",
          JSON.stringify(qualityDataCopy),
        );

        if (signatureFile) {
          regularProjectFormData.append("signature", signatureFile);
        }

        // Step 7: Create the regular project
        const regularProjectResponse = await fetch(
          `${base_url}/v1/createProject`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: regularProjectFormData,
          },
        );

        const regularProjectResult = await regularProjectResponse.json();
        if (regularProjectResult.status !== 1) {
          throw new Error(
            "Fehler beim Erstellen des regulären Projekts: " +
              (regularProjectResult.message || "Unbekannter Fehler"),
          );
        }

        const createdRegularProject = regularProjectResult.data[0];
        const partnerProjectId = createdRegularProject
          ? createdRegularProject._id
          : null;

        // Step 8: Create FormData for the stock project
        const stockProjectFormData = new FormData();
        if (partnerProjectId) {
          stockProjectFormData.append("partnerProjectId", partnerProjectId);
        }
        stockProjectFormData.append("ANR", newStockANR);
        stockProjectFormData.append("Menge", formData.Menge); // Use MengeKundenauftrag as Menge for stock

        stockProjectFormData.append(
          "Auftragsbezeichnung",
          formData.form_auftragsbezeichnung,
        );
        stockProjectFormData.append(
          "Baugruppenzeichnung",
          formData.form_baugruppenzeichnung || "",
        );
        stockProjectFormData.append("Erlauterung", formData.form_erläuterung);
        stockProjectFormData.append("Artikelnummer", formData.Artikelnummer);
        stockProjectFormData.append("MengeCost", formData.MengeCost);
        stockProjectFormData.append(
          "singlemengecost",
          formData.singlemengecost || "0",
        );
        stockProjectFormData.append(
          "MaxLohnaufwand",
          formData.maxLohnaufwand || "0",
        );
        stockProjectFormData.append("maxlohnfundUnit", maxLohnaufwandUnit);
        stockProjectFormData.append("status", formData.status);
        stockProjectFormData.append("percentDone", formData.percentDone);
        stockProjectFormData.append("invoiceNo", formData.invoiceNo);
        stockProjectFormData.append("totalMaterialCost", 0);
        stockProjectFormData.append("totalsubcontratorCost", 0);
        stockProjectFormData.append("kundenID", gabsKunde._id || "");
        stockProjectFormData.append("deadline", formData.deadline || "");
        stockProjectFormData.append(
          "completionDate",
          formData.completionDate || "",
        );
        stockProjectFormData.append(
          "kostenstelle",
          formData.kostenstelle || "",
        );
        stockProjectFormData.append("Lieferdatum", formData.Lieferdatum || "");
        stockProjectFormData.append(
          "Auftragserlös",
          formData.Auftragserlös || "0",
        );
        stockProjectFormData.append(
          "auftragswertNetto",
          formData.auftragswertNetto || "0",
        );

        // Mark as Stock-Auftrag
        stockProjectFormData.append("isStockAuftrag", "true");

        // Add the additional state values
        stockProjectFormData.append("shippingMethod", shippingMethod || "");
        stockProjectFormData.append("packagingType", packagingType || "");

        // Send externalService as individual array items (FormData will handle arrays properly)
        if (externalService && Array.isArray(externalService)) {
          externalService.forEach((service, index) => {
            stockProjectFormData.append(`externalService[${index}]`, service);
          });
        }

        // Create new signature file for stock project if exists
        let stockSignatureFile = null;
        if (signatureFile) {
          // Create a copy of the signature file for the stock project
          const stockBlob = new Blob([await signatureFile.arrayBuffer()], {
            type: "image/png",
          });
          stockSignatureFile = new File(
            [stockBlob],
            `signature_${newStockANR}_${Date.now()}.png`,
            {
              type: "image/png",
            },
          );
        }

        stockProjectFormData.append(
          "qualityNote",
          JSON.stringify(qualityDataCopy),
        );

        if (stockSignatureFile) {
          stockProjectFormData.append("signature", stockSignatureFile);
        }

        // Step 9: Create the stock project
        const stockProjectResponse = await fetch(
          `${base_url}/v1/createProject`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: stockProjectFormData,
          },
        );

        const stockProjectResult = await stockProjectResponse.json();
        if (stockProjectResult.status === 1) {
          toast.success(
            `Beide Aufträge erfolgreich erstellt! Regular ANR: ${newRegularANR} (Menge: ${formData.Menge}), Stock ANR: ${newStockANR} (Menge: ${formData.MengeKundenauftrag})`,
          );
          setPagination((prev) => ({ ...prev, pageIndex: 0 }));
          fetchStockOrders();
          fetchRegularProjects();
          clearForm();
        } else {
          throw new Error(
            "Fehler beim Erstellen des Stock-Auftrags: " +
              (stockProjectResult.message || "Unbekannter Fehler"),
          );
        }
      } catch (error) {
        console.error("Fehler:", error);
        toast.error(
          "Fehler beim Erstellen des Stock-Auftrags: " + error.message,
        );
      }
    },
    [
      formData,
      checkStockANRExists,
      incrementANR,
      fetchStockOrders,
      fetchRegularProjects,
      selectedKunde,
      maxLohnaufwandUnit,
      shippingMethod,
      packagingType,
      externalService,
      qualityData,
      clearForm,
      nextRegularANR,
      nextStockANR,
    ],
  );

  const handleEdit = useCallback(async () => {
    try {
      const response = await fetch(`${base_url}/v1/stock/updateStockOrder`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(editOrder),
      });

      const result = await response.json();
      if (result.status === 1) {
        toast.success("Stock-Auftrag erfolgreich aktualisiert");
        setShowEditModal(false);
        fetchStockOrders();
        fetchRegularProjects();
      } else {
        toast.error("Fehler beim Aktualisieren des Stock-Auftrags");
      }
    } catch (error) {
      console.error("Fehler beim Aktualisieren des Stock-Auftrags:", error);
      toast.error("Fehler beim Aktualisieren des Stock-Auftrags");
    }
  }, [editOrder, fetchStockOrders, fetchRegularProjects]);

  const handleDelete = useCallback(
    async (id) => {
      if (
        window.confirm(
          "Sind Sie sicher, dass Sie diesen Stock-Auftrag löschen möchten?",
        )
      ) {
        try {
          const response = await axios.delete(
            `${base_url}/stock/deleteStockOrder/${id}`,
          );
          if (response.data.success) {
            fetchStockOrders();
            fetchRegularProjects();
          }
        } catch (error) {
          console.error("Fehler:", error);
          toast.error("Fehler beim Löschen des Stock-Auftrags");
        }
      }
    },
    [fetchStockOrders, fetchRegularProjects],
  );

  const printBarcode = useCallback((number, description) => {
    const printWindow = window.open("", "PRINT", "height=800,width=800");
    printWindow.document.write("<html><head><title>Barcode drucken</title>");
    printWindow.document.write(`
        <style>
        @page { size: 8in 4in; margin: 8mm; }
        body { width: 8in; margin: 0; padding: 0; line-height: 1; }
        .barcode { text-align: center; }
        .barcode svg { width: 100% !important; height: 250px !important; }
        .barcode p { font-size: 16px; font-weight: bold; margin: 8px 0; }
      </style>
    `);

    printWindow.document.write("</head><body>");
    const barcodeElement = document.getElementById("barcode-" + number);
    if (barcodeElement) {
      printWindow.document.write('<div class="barcode">');
      printWindow.document.write(barcodeElement.innerHTML);
      printWindow.document.write("<p>" + description + "</p>");
      printWindow.document.write("</div>");
    } else {
      console.error("Barcode-Element nicht gefunden.");
    }
    printWindow.document.write("</body></html>");
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  }, []);

  const BarcodeContainer = useCallback(
    () => (
      <div style={{ display: "none" }}>
        {stockOrders.map((order) => (
          <div key={order._id} id={"barcode-" + order.ANR}>
            <Barcode width={1.4} height={30} value={order.ANR} />
            <div>{order.Auftragsbezeichnung}</div>
          </div>
        ))}
        {regularProjects.map((project) => (
          <div key={project._id} id={"barcode-" + project.ANR}>
            <Barcode width={1.4} height={30} value={project.ANR} />
            <div>{project.Auftragsbezeichnung}</div>
          </div>
        ))}
      </div>
    ),
    [stockOrders, regularProjects],
  );

  // -----------------------------
  // Custom Print Function
  // -----------------------------
  const handleCustomPrint = useCallback(() => {
    if (!selectedProjectForPrint) return;
    const printDate = new Date().toLocaleString();
    const barcodeElement = document.getElementById(
      `barcode-${selectedProjectForPrint.ANR}`,
    );
    const barcodeHTML = barcodeElement ? barcodeElement.innerHTML : "";
    const projectMeta = {
      Gedruckt: printDate,
      Referenz: selectedProjectForPrint.ANR,
      Status: selectedProjectForPrint.status || "N/A",
    };
    const metadataHTML = Object.entries(projectMeta)
      .map(
        ([key, value]) => `
        <div class="metadata-item">
          <span class="metadata-label">${key}:</span>
          <span class="metadata-value">${value}</span>
        </div>
      `,
      )
      .join("");
    const tableRows = selectedPrintFields
      .map((fieldKey) => {
        const field = availablePrintFields.find((f) => f.key === fieldKey);
        let value = selectedProjectForPrint[fieldKey] || "";

        if (fieldKey === "deadline" && value) {
          if (value && value != "undefined") {
            const date = new Date(value);

            value = new Intl.DateTimeFormat("de-DE", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
            }).format(date);
          } else value = "N/A";
        } else if (fieldKey == "MaxLohnaufwand") {
          const rawValue = parseFloat(
            selectedProjectForPrint["MaxLohnaufwand"],
          );
          if (isNaN(rawValue)) {
            value = "0 Stunden";
          } else {
            const unit =
              selectedProjectForPrint["maxlohnfundUnit"] == "minutes"
                ? "Minuten"
                : "Stunden";
            value = `${rawValue} ${unit}`;
          }
        } else if (fieldKey === "externalService") {
          // Handle both array and string formats for external service
          const normalizedValue = normalizeExternalService(value);
          value =
            normalizedValue.length > 0
              ? normalizedValue.join(", ")
              : "Keine Fremdleistungen";
        } else if (typeof value === "number") {
          value = value.toLocaleString();
        } else if (Array.isArray(value)) {
          value = value.join(", ");
        } else if (fieldKey === "qualityNote") {
          if (value && typeof value === "object") {
            const qualityNote = value;
            let qualityHtml =
              '<div style="font-size: 0.9em; line-height: 1.4;">';

            if (qualityNote.datum) {
              const qualityDate = new Date(qualityNote.datum);
              const formattedDate = new Intl.DateTimeFormat("de-DE", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
              }).format(qualityDate);
              qualityHtml += `<div><strong>Datum:</strong> ${formattedDate}</div>`;
            }

            if (qualityNote.werkstucke) {
              qualityHtml += `<div><strong>Anzahl Werkstücke:</strong> ${qualityNote.werkstucke}</div>`;
            }

            if (qualityNote.untershift) {
              qualityHtml += `<div><strong>Unterschrift:</strong> ✓ Vorhanden</div>`;
              if (
                typeof qualityNote.untershift === "string" &&
                (qualityNote.untershift.startsWith("http") ||
                  qualityNote.untershift.startsWith("data:"))
              ) {
                qualityHtml += `<div style="margin-top: 8px;">
                  <img src="${qualityNote.untershift}" 
                       alt="Unterschrift" 
                       style="max-width: 150px; max-height: 90px; border: 1px solid #ddd; border-radius: 4px;" />
                </div>`;
              }
            } else {
              qualityHtml += `<div><strong>Unterschrift:</strong> ✗ Nicht vorhanden</div>`;
            }

            qualityHtml += "</div>";
            value = qualityHtml;
          } else {
            value = "Keine Qualitätsdaten vorhanden";
          }
        }
        return `
          <tr>
            <th class="field-label">${field.label}</th>
            <td class="field-value">${value}</td>
          </tr>
        `;
      })
      .join("");
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Auftrag ${selectedProjectForPrint.ANR} - Details</title>
          <style>
            @media print {
              @page { size: A4; margin: 15mm; }
              body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
              .no-print { display: none !important; }
            }
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; background: #fff; }
            .watermark { position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%) rotate(-45deg); font-size: 100px; opacity: 0.03; z-index: -1; pointer-events: none; }
            .container { max-width: 100%; margin: 0 auto; padding: 20px; }
            .header { padding: 20px 0; border-bottom: 2px solid #2c3e50; margin-bottom: 30px; text-align: center; }
            .header h1 { margin: 0; color: #2c3e50; font-size: 24px; }
            .metadata-section { background: #f8f9fa; padding: 15px; border-radius: 4px; margin-bottom: 30px; display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 15px; }
            .metadata-item { display: flex; flex-direction: column; gap: 5px; }
            .metadata-label { font-size: 0.8em; color: #666; text-transform: uppercase; }
            .metadata-value { font-weight: 500; }
            .barcode-section { text-align: center; margin: 20px 0; padding: 15px; background: #fff; border: 1px solid #eee; border-radius: 4px; }
            .data-table { width: 100%; border-collapse: collapse; margin: 20px 0; background: #fff; box-shadow: 0 1px 3px rgba(0,0,0,0.1); }
            .data-table th, .data-table td { padding: 12px 15px; text-align: left; border: 1px solid #ddd; }
            .field-label { background: #f8f9fa; font-weight: 600; color: #2c3e50; width: 30%; }
            .field-value { color: #333; }
            .footer { position: fixed; bottom: 0; left: 0; right: 0; padding: 10px; background: #f8f9fa; border-top: 1px solid #ddd; font-size: 0.8em; color: #666; text-align: center; }
          </style>
        </head>
        <body>
          <div class="watermark">VERTRAULICH</div>
          <div class="container">
            <header class="header">
              <h1>Auftragsdetails</h1>
            </header>
            <div class="metadata-section">
              ${metadataHTML}
            </div>
            <div class="barcode-section">
              ${barcodeHTML}
            </div>
            <table class="data-table">
              ${tableRows}
            </table>
            <footer class="footer">
              <div>Gedruckt am ${printDate} | Vertrauliches Dokument</div>
            </footer>
          </div>
        </body>
      </html>
    `;
    const printWindow = window.open("", "", "width=842,height=595");
    if (!printWindow) {
      console.error("Fehler beim Öffnen des Druckfensters.");
      return;
    }
    printWindow.document.open();
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.onafterprint = () => {
          printWindow.close();
          setIsPrintDialogOpen(false);
        };
      }, 1000);
    };
  }, [selectedProjectForPrint, selectedPrintFields, availablePrintFields]);

  // -----------------------------
  // Table Columns Definition
  // -----------------------------
  const columns = useMemo(
    () => [
      {
        accessorKey: "ANR",
        header: "Stock-ANR",
        size: 150,
        Cell: ({ row }) => (
          <span className="text-blue-600 font-medium">{row.original.ANR}</span>
        ),
      },
      {
        accessorKey: "Auftragsbezeichnung",
        header: "Auftragsbezeichnung",
        size: 200,
      },
      {
        accessorKey: "Menge",
        header: "Menge Kundenauftrag",
        size: 100,
      },
      {
        accessorKey: "MengeCost",
        header: "Stückkosten",
        size: 120,
        Cell: ({ row }) => (
          <span>€{Number(row.original.MengeCost || 0).toFixed(2)}</span>
        ),
      },
      {
        accessorKey: "Lieferdatum",
        header: "Lieferdatum",
        size: 120,
        Cell: ({ row }) => (
          <span>
            {row.original.Lieferdatum
              ? new Date(row.original.Lieferdatum).toLocaleDateString("de-DE")
              : "Nicht gesetzt"}
          </span>
        ),
      },
      {
        accessorKey: "actions",
        header: "Aktionen",
        Cell: ({ row }) => (
          <div className="flex space-x-2">
            <button
              onClick={() => {
                setEditOrder(row.original);
                setShowEditModal(true);
              }}
              className="p-1 text-blue-600 hover:text-blue-900"
            >
              <Edit className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleDelete(row.original._id)}
              className="p-1 text-red-600 hover:text-red-900"
            >
              <Trash2 className="w-4 h-4" />
            </button>
            <button
              onClick={() =>
                printBarcode(row.original.ANR, row.original.Auftragsbezeichnung)
              }
              className="p-1 text-gray-600 hover:text-gray-900"
            >
              <Printer className="w-4 h-4" />
            </button>
            <button
              onClick={() => {
                setSelectedProjectForPrint(row.original);
                setSelectedPrintFields(
                  availablePrintFields.map((field) => field.key),
                );
                setIsPrintDialogOpen(true);
              }}
              className="p-1 text-green-600 hover:text-green-900"
              title="Benutzerdefinierter Druck"
            >
              <Printer className="w-4 h-4" />
            </button>
          </div>
        ),
      },
    ],
    [stockOrders, handleDelete, printBarcode],
  );

  // -----------------------------
  // Modal Render Functions
  // -----------------------------
  const renderEditModal = () => {
    if (!showEditModal) return null;
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium">Stock-Auftrag bearbeiten</h3>
            <button
              onClick={() => setShowEditModal(false)}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Stock-ANR
              </label>
              <input
                type="text"
                value={editOrder?.ANR || ""}
                disabled
                className="mt-1 block w-full rounded-none border-gray-300 bg-gray-50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Auftragsbezeichnung
              </label>
              <input
                type="text"
                value={editOrder?.Auftragsbezeichnung || ""}
                onChange={(e) =>
                  setEditOrder({
                    ...editOrder,
                    Auftragsbezeichnung: e.target.value,
                  })
                }
                className="mt-1 block w-full rounded-none border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Menge Kundenauftrag
              </label>
              <input
                type="number"
                value={editOrder?.Menge || ""}
                onChange={(e) =>
                  setEditOrder({
                    ...editOrder,
                    Menge: e.target.value,
                  })
                }
                className="mt-1 block w-full rounded-none border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Stückkosten
              </label>
              <input
                type="number"
                step="0.01"
                value={editOrder?.MengeCost || ""}
                onChange={(e) =>
                  setEditOrder({
                    ...editOrder,
                    MengeCost: e.target.value,
                  })
                }
                className="mt-1 block w-full rounded-none border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
          </div>
          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={() => setShowEditModal(false)}
              className="px-4 py-3 border border-gray-300 rounded-none shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Abbrechen
            </button>
            <button
              onClick={handleEdit}
              className="px-4 py-3 border border-transparent rounded-none shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              Änderungen speichern
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderPrintDialog = () => {
    if (!isPrintDialogOpen) return null;
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-md">
          <h2 className="text-xl font-bold mb-4">
            Felder zum Drucken auswählen
          </h2>
          <div className="space-y-2">
            {availablePrintFields.map((field) => (
              <label key={field.key} className="flex items-center">
                <input
                  type="checkbox"
                  checked={selectedPrintFields.includes(field.key)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedPrintFields((prev) => [...prev, field.key]);
                    } else {
                      setSelectedPrintFields((prev) =>
                        prev.filter((key) => key !== field.key),
                      );
                    }
                  }}
                  className="mr-2"
                />
                {field.label}
              </label>
            ))}
          </div>
          <div className="mt-4 flex justify-end space-x-2">
            <button
              onClick={() => setIsPrintDialogOpen(false)}
              className="px-4 py-3 bg-gray-300 rounded-none"
            >
              Abbrechen
            </button>
            <button
              onClick={handleCustomPrint}
              className="px-4 py-3 bg-blue-600 text-white rounded-none"
            >
              Drucken
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderKundeModal = () => {
    if (!showKundeModal) return null;
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-md relative">
          <button
            onClick={() => setShowKundeModal(false)}
            className="absolute top-2 right-2 text-gray-400 hover:text-gray-500"
          >
            <X className="w-6 h-6" />
          </button>
          {!selectedKunde ? (
            <>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Kunden suchen
              </h2>
              <div className="relative">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Suche nach Kundenname oder Kontonummer"
                  className="w-full px-4 py-3 border border-gray-300 rounded-none focus:ring-blue-500 focus:border-blue-500"
                />
                {searchTerm && filteredKunden.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-none shadow-lg max-h-60 overflow-auto">
                    {filteredKunden.map((kunde) => (
                      <div
                        key={kunde._id}
                        onClick={() => handleSelectKunde(kunde)}
                        className="px-4 py-3 hover:bg-gray-100 cursor-pointer border-b last:border-b-0"
                      >
                        <div className="font-medium text-gray-900">
                          {kunde.bezeichnung}
                        </div>
                        <div className="text-sm text-gray-600 mt-1">
                          Konto: {kunde.konto}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">
                    Ausgewählter Kunde
                  </h3>
                  <p className="text-gray-500 mt-1">
                    {confirmed
                      ? "Kunde bestätigt"
                      : "Bitte bestätigen Sie Ihre Auswahl"}
                  </p>
                </div>
                <button
                  onClick={handleClearKunde}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Zurücksetzen
                </button>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <h4 className="text-lg font-medium text-gray-900">
                  {selectedKunde.bezeichnung}
                </h4>
                <p className="text-gray-600 mt-2">
                  Konto: {selectedKunde.konto}
                </p>
              </div>
              {!confirmed && (
                <button
                  onClick={handleConfirmKunde}
                  className="w-full bg-blue-600 text-white py-3 px-4 rounded-none hover:bg-blue-700 transition-colors"
                >
                  Auswahl bestätigen
                </button>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  // -----------------------------
  // Main Render
  // -----------------------------
  return (
    <Box m="20px">
      <div className="min-h-screen py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Heading */}
          <div className="mb-8 text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {getPageHeading()}
            </h1>
            <p className="text-gray-600">
              {location.pathname.includes("/stock/addAuftrage")
                ? "Erstellen Sie neue Lager-Einträge"
                : location.pathname.includes("/stock/stockAnr")
                  ? "Erstellen Sie Aufträge und Lager-Einträge gleichzeitig"
                  : "Erstellen Sie neue Aufträge für Ihre Projekte"}
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Side: Stock ANR Display & Form */}
            <div>
              <StockANRDisplay
                // anrParts={anrParts}
                // handleIncrementFirst={handleIncrementFirst}
                // handleDecrementFirst={handleDecrementFirst}
                // handleIncrementSecond={handleIncrementSecond}
                // handleDecrementSecond={handleDecrementSecond}
                // handleIncrementThird={handleIncrementThird}
                // handleDecrementThird={handleDecrementThird}
                copyANR={copyStockANR}
                color={colors.primary[400]}
                printStockDocumentsBarcode={printStockDocumentsBarcode} // Add these props
                clearForm={clearForm}
              />

              {/* New navigation buttons section - same as AddAnr */}
              <div className="flex justify-center space-x-4 my-4">
                <button
                  onClick={navigateToStockAuftraege}
                  className="flex-1 p-3 bg-blue-600 text-white hover:bg-blue-700 transition-colors rounded font-medium shadow-md"
                >
                  Aufträge
                </button>
                <button
                  onClick={() => {
                    navigate("/stock/addAuftrage");
                  }}
                  className="flex-1 p-3 bg-green-600 text-white hover:bg-green-700 transition-colors rounded font-medium shadow-md"
                >
                  Lager Aufträge
                </button>
              </div>

              <Card
                elevation={3}
                sx={{ bgcolor: colors.primary[400], transition: "all 0.3s" }}
              >
                <div className="p-6">
                  <StockForm
                    formData={formData}
                    setFormData={setFormData}
                    handleSubmit={handleSubmit}
                    selectedKunde={selectedKunde}
                    setShowKundeModal={setShowKundeModal}
                    maxLohnaufwandUnit={maxLohnaufwandUnit}
                    setMaxLohnaufwandUnit={setMaxLohnaufwandUnit}
                    shippingMethod={shippingMethod}
                    setShippingMethod={setShippingMethod}
                    packagingType={packagingType}
                    setPackagingType={setPackagingType}
                    externalService={externalService}
                    setExternalService={setExternalService}
                    handleQualityDataChange={handleQualityDataChange}
                    editMode={false}
                    initialExternalService={[]}
                    latestANR={latestANR}
                    latestStockANR={latestStockANR}
                    nextRegularANR={nextRegularANR}
                    nextStockANR={nextStockANR}
                  />
                </div>
              </Card>
            </div>

            {/* Right Side: Recently Created Orders */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Stock Orders Section */}
              <Card
                elevation={3}
                sx={{ bgcolor: colors.primary[400], transition: "all 0.3s" }}
              >
                <div className="p-6">
                  <h2 className="text-xl font-semibold mb-4">
                    Kürzlich erstellte Stock-Aufträge
                  </h2>
                  <div className="space-y-4">
                    {stockOrders.slice(0, 5).map((order) => (
                      <div
                        key={order._id}
                        className="p-4 border rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200"
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-medium text-blue-600">
                              {order.ANR}
                            </h3>
                            <p className="text-sm text-gray-500">
                              {order.Auftragsbezeichnung}
                            </p>
                            <p className="text-xs text-gray-400">
                              Menge: {order.Menge || "N/A"}
                            </p>
                          </div>
                          <div>
                            <button
                              onClick={() =>
                                printBarcode(
                                  order.ANR,
                                  order.Auftragsbezeichnung,
                                )
                              }
                              className="text-blue-600 hover:text-blue-800"
                            >
                              <Printer className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                    {stockOrders.length === 0 && (
                      <p className="text-gray-500 text-center py-4">
                        Keine Stock-Aufträge gefunden
                      </p>
                    )}
                  </div>
                </div>
              </Card>

              {/* Regular Projects Section */}
              <Card
                elevation={3}
                sx={{ bgcolor: colors.primary[400], transition: "all 0.3s" }}
              >
                <div className="p-6">
                  <h2 className="text-xl font-semibold mb-4">
                    Kürzlich erstellte Reguläre Aufträge
                  </h2>
                  <div className="space-y-4">
                    {regularProjects.slice(0, 5).map((project) => (
                      <div
                        key={project._id}
                        className="p-4 border rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200"
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-medium text-green-600">
                              {project.ANR}
                            </h3>
                            <p className="text-sm text-gray-500">
                              {project.Auftragsbezeichnung}
                            </p>
                            <p className="text-xs text-gray-400">
                              Menge: {project.Menge || "N/A"}
                            </p>
                          </div>
                          <div>
                            <button
                              onClick={() =>
                                printBarcode(
                                  project.ANR,
                                  project.Auftragsbezeichnung,
                                )
                              }
                              className="text-green-600 hover:text-green-800"
                            >
                              <Printer className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                    {regularProjects.length === 0 && (
                      <p className="text-gray-500 text-center py-4">
                        Keine regulären Aufträge gefunden
                      </p>
                    )}
                  </div>
                </div>
              </Card>
            </div>
          </div>

          <div style={{ display: "none" }}>
            <div id="barcode-stock-documents">
              <Barcode width={1.4} height={30} value="post-documents" />
            </div>
          </div>

          {/* Stock Orders Table */}
          <div className="mt-8 rounded-lg shadow-sm">
            <MaterialReactTable
              columns={columns}
              data={stockOrders}
              enableColumnFilters
              enableColumnOrdering
              enablePagination={true}
              enableSorting
              manualPagination={true}
              rowCount={rowCount}
              pageCount={totalPages}
              onPaginationChange={setPagination}
              state={{
                isLoading: loading,
                pagination,
              }}
              initialState={{
                pagination: {
                  pageIndex: 0,
                  pageSize: 25,
                },
              }}
              muiPaginationProps={{
                showRowsPerPage: true,
                showFirstLastPageButtons: true,
                rowsPerPageOptions: [10, 25, 50, 100],
                labelDisplayedRows: ({ from, to, count }) =>
                  `${from}-${to} von ${
                    count !== -1 ? count : `mehr als ${to}`
                  } (Seite ${pagination.pageIndex + 1} von ${totalPages})`,
                labelRowsPerPage: "Zeilen pro Seite:",
              }}
            />
          </div>
        </div>
        {/* Modals */}
        {renderEditModal()}
        {renderPrintDialog()}
        {renderKundeModal()}
        {/* Hidden Barcode Container */}
        <BarcodeContainer />
        <ToastContainer />
      </div>
    </Box>
  );
}

export default StockAnr;
